import { FormattedMessage } from 'react-intl'
import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import setQuery from 'set-query-string'
import moment from 'moment'

import { useDueNow } from '../../hooks'
import IntentPopup from '../checkout/IntentPopup'
import PaymentSchedule from '../checkout/PaymentSchedule'
import useCurrentShop from './hooks/useCurrentShop'
import useTrip from './hooks/useTrip'
import PropTypes from 'prop-types'

import {
  BookingDetailsCardFooter, BookingDetailsCardHeader, BookingDetailsCardMain, BookingDetailsCardSummary,
} from '../account/booking-details/booking-details-card'
import { showLoader } from '../../actions/main-actions'
import useCheckoutSummary from './hooks/useCheckoutSummary'
import store from 'store2'
import { getAffiliateId, parseQuery, roundPrice } from '../../utils'
import CheckoutLastStepForm from '../checkout/CheckoutLastStepForm'
import useCheckoutForm from './hooks/useCheckoutForm'
import Occupation from './Occupation/Occupation'
import { formatDate } from '../helpers/DateFormat'
import { convertCurrency } from '../helpers'
import { pushToDataLayer, trackGA4Ecommerce } from '../../gtm'
import useGTM from './hooks/useGTM'

const query = parseQuery()
// TODO Rename it later
const affiliateId = query.aid || getAffiliateId()?.aid
const referrer = query.referrer || store.session('diviac.referrer')
const uuid = query.order_uuid || store.session('diviac.order_uuid')

if(uuid) {
  store.session('diviac.order_uuid', uuid)
}
if(referrer) {
  store.session('diviac.referrer', referrer)
}
export default function Step4({
  tripId,
  setOrder,
  countryCode
}) {
  const dispatch = useDispatch()
  const { setGtmDataForInclusions } = useGTM()
  const { shopInfo } = useCurrentShop()
  const {
    dueNowData,
    getDueNowData,
  } = useDueNow()
  const {
    selectedTrip,
    requirements,
  } = useTrip()
  const {
    guestsSplitting,
    checkoutInclusions,
    roomParams,
    totalPrice,
  } = useSelector(({
    guestsSplitting,
    checkoutInclusions,
    roomParams,
    selectedCabins,
    totalPrice,
  }) => ({
    guestsSplitting,
    checkoutInclusions,
    roomParams,
    cabins: selectedCabins,
    totalPrice,
  }))

  const { checkoutSummary } = useCheckoutSummary(dueNowData, selectedTrip, shopInfo, roomParams, checkoutInclusions)

  const { sendForm } = useCheckoutForm(uuid, referrer, dueNowData, setOrder, requirements, selectedTrip.duration,
    roomParams.divers, affiliateId)

  const onVoucherAdd = (coupon) => {
    dispatch(showLoader(true))
    getDueNowData(shopInfo.id, dueNowData.selectedInclusions, {
      ...selectedTrip,
      roomParams,
    }, coupon.id).then(() => {
      dispatch(showLoader(false))
    })
  }

  const onCouponRemove = () => {
    dispatch(showLoader(true))
    getDueNowData(shopInfo.id, dueNowData.selectedInclusions, selectedTrip).then(() => {
      dispatch(showLoader(false))
    })
  }

  useEffect(() => {
    const query = parseQuery() || {}
    if(shopInfo && checkoutInclusions && selectedTrip && guestsSplitting) {
      setQuery(null)
      const cabinPrices = guestsSplitting.reduce((acc, item) => {
        item.pricing.forEach((pricing) => {
          acc.push({
            ...pricing,
            pricing: pricing.id,
            quantity: pricing.qty,
            isRoom: true,
          })
        })
        return acc
      }, [])
      const queryParams = {
        dateFrom: formatDate(selectedTrip.dateFrom, 'YYYY-MM-DD'),
        dateTo: formatDate(selectedTrip.dateTo, 'YYYY-MM-DD'),
        productId: tripId,
        savedOrder: true,
        ...query,
      }
      const cabinPricing = []
      guestsSplitting.forEach((cabins) => {
        const splitted = []
        cabins.cart.forEach((cart) => {
          const items = []
          cart.forEach((cartItem) => {
            items.push({
              id: cartItem.id,
              qty: cartItem.qty,
            })
          })
          splitted.push(items)
        })
        cabinPricing.push(splitted)
      })
      queryParams.cabinPricing = JSON.stringify(cabinPricing)
      checkoutInclusions.forEach((item) => {
        if(item.diverNumber) {
          /* eslint-disable standard/computed-property-even-spacing */
          queryParams[`incl_${item.pricing}_${item.diverNumber - 1}_${item.guestType}`] = item.quantity
        } else if(item.divingItem) {
          /* eslint-disable standard/computed-property-even-spacing */
          queryParams[`diving_${item.pricing}_${item.key}_${item.numberOfInstances}`] = item.quantity
        } else if(!item.isRoom) {
          queryParams[`incl_${item.pricing}`] = item.quantity
        }
      })

      setQuery(queryParams, { clear: true })
      getDueNowData(shopInfo.id, [...checkoutInclusions, ...cabinPrices], {
        ...selectedTrip,
        roomParams,
        isConfirmed: true,
      })

      const convertedPrice = roundPrice(convertCurrency(totalPrice, shopInfo.currency, 'USD'))

      pushToDataLayer({
        checkout_order_amount: convertedPrice,
        checkout_cart_value: convertedPrice,
      })

      const { GA4Products } = setGtmDataForInclusions(checkoutInclusions)
      guestsSplitting.forEach((cabin) => {
        cabin.pricing.forEach((pricing) => {
          GA4Products.push({
            item_id: pricing.id.toString(),
            price: Number(roundPrice(convertCurrency(pricing.priceNet, shopInfo.currency, 'USD'))),
            item_variant: `${selectedTrip.itinerary.title} / ${cabin.titlte} / ${moment(pricing.dateFrom)
              .utc()
              .locale('en')
              .format('DD MMM')}-${moment(pricing.dateTo)
              .utc()
              .locale('en')
              .format('DD MMM')} / ${pricing.isDiver ? `${pricing.qty} diver(s)` : `${pricing.qty} non-diver(s)`}`,
            item_brand: 'PADI',
            item_name: shopInfo.title,
            item_category: 'PADI Travel',
            item_category2: 'Liveaboard',
            item_list_name: 'PADI Travel Liveaboard - Product Page',
            quantity: pricing.qty,
          })
        })
      })
      if(dueNowData?.taxFreeAmount) {
        trackGA4Ecommerce('begin_checkout', {
          currency: 'USD',
          value: Number(convertCurrency(dueNowData?.taxFreeAmount, dueNowData?.currency, 'USD').toFixed(2)),
          items: GA4Products,
        }, 'PADI Travel - Liveaboards')
      }

      store.session.set('ecommerce', GA4Products)
    }
  }, [shopInfo, checkoutInclusions, guestsSplitting, selectedTrip, totalPrice, dueNowData?.taxFreeAmount])

  return (<div className='step3'>
    <div className='complete-wrap'>
      <h2 className='step-title'>
        <span className='number'>4</span>
        <FormattedMessage
          defaultMessage='Enter your information and book'
          id='enter_info_and_book'
        />
      </h2>
      <div className='order-total'>
        <div className='pan-white'>
          <BookingDetailsCardHeader order={checkoutSummary} />
          <div className='pad'>
            <Occupation checkoutSummary={checkoutSummary} />
            <BookingDetailsCardSummary order={checkoutSummary} />
            <BookingDetailsCardMain
              order={checkoutSummary}
              onCouponRemove={onCouponRemove}
              isCheckout
            />
            <BookingDetailsCardFooter
              order={checkoutSummary}
              showCouponForm
              onVoucherAdd={onVoucherAdd}
            />
            <PaymentSchedule
              dueNowData={dueNowData}
              freeCancellationDays={selectedTrip.freeCancellationDays}
            />
          </div>
        </div>
      </div>

      <CheckoutLastStepForm
        onCheckoutFormSubmit={sendForm}
        orderEdit={!!uuid}
        selectedTripData={selectedTrip}
        countryCode={countryCode}
      />
      <IntentPopup
        isLiveabord
        shopTitle={shopInfo.title}
        itineraryTitle={selectedTrip?.itinerary?.title}
        dateFrom={selectedTrip.dateFrom}
        dateTo={selectedTrip.dateTo}
        dueNowData={dueNowData?.payload}
        guestsSplitting={guestsSplitting}
        countryCode={countryCode}
      />
    </div>
  </div>)
}

Step4.propTypes = {
  tripId: PropTypes.string,
  id: PropTypes.string,
  setOrder: PropTypes.func,
}
